import { Badge } from "@/components/ui";
import { DataTableColumnHeader } from "@/components/ui/shadcn/DataTable/DataTableColumnHeader";
import type { Matter, MatterEmailWithAttachments } from "@/lib/types";
import { convertToLocalFromDatabaseDateTime } from "@/lib/utils/dateUtils";
import { formatDateTime } from "@/lib/utils/format";
import {
  getMatterEmailAttachmentName,
  getMatterEmailDirection,
  type MatterEmailDirection,
} from "@/lib/utils/matterEmail";
import { createColumnHelper, type ColumnDef } from "@tanstack/react-table";

export type MatterEmailTableRow = MatterEmailWithAttachments & {
  attachmentsCopiedToRepo: boolean;
  direction: MatterEmailDirection;
  filesCopiedCount: number;
  filesToCopyCount: number;
};

const columnHelper = createColumnHelper<MatterEmailTableRow>();

const formatEmailList = (emails?: string[] | null) => {
  if (!emails || emails.length === 0) {
    return "-";
  }
  return emails.join(", ");
};

export const createMatterEmailsColumns = (): ColumnDef<MatterEmailTableRow>[] =>
  [
    columnHelper.accessor("dateCreated", {
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      id: "Date Created",
      sortingFn: "customDateTime",
      cell: ({ getValue }) => {
        const value = getValue();
        if (!value) return "-";
        return formatDateTime(convertToLocalFromDatabaseDateTime(value), false);
      },
    }),
    columnHelper.accessor("subject", {
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      id: "Subject",
      enableHiding: false,
      cell: ({ getValue }) => getValue() || "(No subject)",
    }),
    columnHelper.accessor("fromEmails", {
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      id: "From",
      cell: ({ getValue }) => formatEmailList(getValue()),
    }),
    columnHelper.accessor("toEmails", {
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      id: "To",
      cell: ({ getValue }) => formatEmailList(getValue()),
    }),
    columnHelper.accessor("ccEmails", {
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      id: "CC",
      cell: ({ getValue }) => formatEmailList(getValue()),
    }),
    columnHelper.accessor("attachmentsCopiedToRepo", {
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      id: "Files Copied",
      cell: ({ getValue, row }) => {
        const hasAttachments = row.original.attachments.some((attachment) => {
          const name = attachment.name?.toLowerCase() ?? "";
          return name && !name.endsWith(".eml");
        });
        if (!hasAttachments) {
          return "N/A";
        }
        const allCopied = getValue();
        if (allCopied) {
          return <Badge variant="secondary">All files copied</Badge>;
        }
        return (
          <Badge variant="muted">
            {row.original.filesCopiedCount}/{row.original.filesToCopyCount}{" "}
            files copied
          </Badge>
        );
      },
    }),
    columnHelper.display({
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      id: "Attachment Count",
      cell: ({ row }) => {
        const count = row.original.attachments.filter((attachment) => {
          const name = attachment.name?.toLowerCase();
          return name && !name.endsWith(".eml");
        }).length;
        return count === 0 ? "-" : count;
      },
    }),
    columnHelper.display({
      header: ({ column }) => <DataTableColumnHeader column={column} />,
      id: "Attachments",
      cell: ({ row }) => {
        const nonEmailAttachments = row.original.attachments.filter(
          (attachment) => {
            const name = attachment.name?.toLowerCase();
            return name && !name.endsWith(".eml");
          },
        );
        if (nonEmailAttachments.length === 0) {
          return "-";
        }

        return (
          <div className="flex flex-wrap gap-1">
            {nonEmailAttachments.map((attachment) => (
              <Badge key={attachment.id} variant="outline">
                {getMatterEmailAttachmentName(attachment)}
              </Badge>
            ))}
          </div>
        );
      },
    }),
  ] as ColumnDef<MatterEmailTableRow>[];

export function buildMatterEmailTableRows(
  matter: Matter,
): MatterEmailTableRow[] {
  const matterFileKeys = new Set<string>();
  matter.files.forEach((file) => {
    if (file.presignedUrlKey) {
      matterFileKeys.add(file.presignedUrlKey);
    }
  });

  const attachmentsByEmailId = matter.emailAttachments.reduce<
    Partial<Record<string, MatterEmailWithAttachments["attachments"]>>
  >((acc, attachment) => {
    if (!attachment.emailId) {
      return acc;
    }
    const existing = acc[attachment.emailId] ?? [];
    existing.push(attachment);
    acc[attachment.emailId] = existing;
    return acc;
  }, {});

  return matter.emails.map((email) => {
    const attachments = attachmentsByEmailId[email.id] ?? [];
    const fileAttachments = attachments.filter((attachment) => {
      const name = attachment.name?.toLowerCase() ?? "";
      if (!attachment.presignedUrlKey) {
        return false;
      }
      if (!name) {
        return false;
      }
      return !name.endsWith(".eml");
    });

    const filesCopiedCount = fileAttachments.reduce((count, attachment) => {
      const key = attachment.presignedUrlKey;
      if (key && matterFileKeys.has(key)) {
        return count + 1;
      }
      return count;
    }, 0);

    const filesToCopyCount = fileAttachments.length;

    const allCopied =
      filesToCopyCount === 0 || filesCopiedCount === filesToCopyCount;

    return {
      ...email,
      attachments,
      attachmentsCopiedToRepo: allCopied,
      direction: getMatterEmailDirection(email, matter.emailAddress),
      filesCopiedCount,
      filesToCopyCount,
    };
  });
}
